import React, { useEffect, useState } from "react";
import { useDebounce } from "use-debounce";
import Swal from "sweetalert2";
import { MagnifyingGlassIcon, XMarkIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { adminApi } from "../../repositories/admin";
import Pagination from "../../components/common/Pagination";
import PageHeader from "../../components/ui/PageHeader";
import { Skeleton } from "../../components/ui/skeleton";
import { HOME_CRUMB } from "../../components/ui/Breadcrumbs";
import DataTable, { DataTableColumn } from "../../components/ui/DataTable";
import Avatar from "../../components/ui/Avatar";

interface MasterRosterArtist {
  id: string;
  artistName: string;
  photo?: string;
  label?: string;
  distributor?: string;
  companyName?: string;
  labelName?: string;
  userEmail?: string;
  pitchCount?: number;
}

const MasterRoster: React.FC = () => {
  const [artists, setArtists] = useState<MasterRosterArtist[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchType, setSearchType] = useState<"artist" | "company">("artist");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch] = useDebounce(searchTerm, 400);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    loadArtists();
    // searchType is intentionally NOT a dependency: changing the type clears the
    // term, so refetching would just re-request the unfiltered list. The next
    // keystroke fetches with the new type (loadArtists reads it directly).
  }, [page, limit, debouncedSearch]);

  const loadArtists = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getAllArtists({
        page,
        limit,
        search: searchType === "artist" ? debouncedSearch : "",
        company: searchType === "company" ? debouncedSearch : "",
      });

      const data = response.data?.data ?? {};
      const list: MasterRosterArtist[] =
        data.artists ?? data.results ?? data.items ?? [];
      setArtists(list);

      const pagination = data.pagination ?? {};
      setTotalPages(pagination.totalPages ?? 1);
      setTotalResults(pagination.totalResults ?? list.length);
    } catch (error: any) {
      console.error("Error loading master roster:", error);
      Swal.fire({
        title: "Error",
        text:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to load artists. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
      setArtists([]);
      setTotalPages(1);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  };

  const getCompany = (artist: MasterRosterArtist) =>
    artist.companyName || "—";

  const columns: DataTableColumn<MasterRosterArtist>[] = [
    {
      key: "artist",
      header: "Artist",
      // Avatar + name + email in one cell, matching the identity-cell pattern
      // used by the dashboard's users table.
      cell: (artist) => (
        <div className="flex items-center gap-3">
          <Avatar
            name={artist.artistName}
            email={artist.userEmail}
            src={artist.photo}
            className="w-10 h-10"
          />
          <div className="min-w-0">
            <div className="font-medium text-black truncate">{artist.artistName}</div>
            {artist.userEmail && (
              <div className="text-xs truncate text-neutral-500">{artist.userEmail}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "company",
      header: "Company",
      cell: (artist) => <span className="text-neutral-600">{getCompany(artist)}</span>,
    },
    {
      key: "distributor",
      header: "Distributor",
      cell: (artist) => <span className="text-neutral-600">{artist.distributor || "—"}</span>,
    },
    {
      key: "pitches",
      header: "Pitches",
      cell: (artist) => <span className="tabular-nums">{artist.pitchCount ?? 0}</span>,
    },
  ];

  return (
    <div className="py-2 sm:p-6">
      <PageHeader
        breadcrumbs={[HOME_CRUMB, { label: "Master Roster" }]}
        className="mb-6"
        title="Master Roster"
        description="All artists across all client rosters."
      />

      {/*
        Section header: entity count on the left, inline search on the right.
        White bordered strip that reads as the table's own header rather than a
        separate grey band.
      */}
      <div className="flex flex-col gap-3 px-4 py-3 bg-white border rounded-t-lg border-neutral-200 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-base font-semibold text-black sm:text-lg">
          Artists
          {loading ? (
            <Skeleton className="inline-block w-16 h-4 ml-2 align-middle" />
          ) : (
            <span className="ml-2 text-sm font-normal text-neutral-500">
              ({totalResults} {totalResults === 1 ? "artist" : "artists"})
            </span>
          )}
        </h3>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          {/* Single-border search bar, as on Contacts / Past Submissions */}
          <div className="flex items-stretch w-full bg-white border rounded-lg border-neutral-300 focus-within:border-primary-500 sm:w-[420px]">
            <div className="relative shrink-0">
              <select
                value={searchType}
                onChange={(e) => {
                  setSearchType(e.target.value as "artist" | "company");
                  setSearchTerm("");
                }}
                aria-label="Search artists by field"
                className="w-[104px] sm:w-auto h-full py-2 pl-3 text-sm truncate bg-transparent border-0 appearance-none cursor-pointer pr-7 text-neutral-600 focus:outline-none focus:ring-0"
              >
                <option value="artist">Artist Name</option>
                <option value="company">Company Name</option>
              </select>
              <ChevronDownIcon className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none right-2 top-1/2 text-neutral-400" />
            </div>

            <span aria-hidden="true" className="self-stretch w-px my-1.5 bg-neutral-200" />

            <div className="relative flex-1 min-w-0">
              <MagnifyingGlassIcon className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none left-3 top-1/2 text-neutral-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={`Search by ${searchType === "artist" ? "artist name" : "company name"}...`}
                className="w-full py-2 text-sm text-black bg-transparent border-0 rounded-r-lg pl-9 pr-9 placeholder-neutral-400 focus:outline-none focus:ring-0"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  aria-label="Clear search"
                  className="absolute p-0.5 -translate-y-1/2 rounded right-2 top-1/2 text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <DataTable
        className="rounded-t-none border-t-0"
        columns={columns}
        rows={artists}
        getRowKey={(artist) => artist.id}
        loading={loading}
        skeletonRows={limit}
        emptyMessage={
          <span className="block">
            <span className="block text-sm font-medium text-black">No artists found</span>
            <span className="block mt-1 text-sm text-neutral-500">
              Try adjusting your search filters.
            </span>
          </span>
        }
      />

      {/* Shared Pagination, with the totals so it can show the range line. */}
      {totalPages > 1 && (
        <div className="mt-4">
          <Pagination
            page={page}
            totalPages={totalPages}
            setPage={setPage}
            totalItems={totalResults}
            limit={limit}
          />
        </div>
      )}
    </div>
  );
};

export default MasterRoster;
